import {
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  type LayerDescriptor,
  type LayerFolder,
  type OverlayGroup,
} from "../components/LayerGroupControl";
import { SEAMLESS_PHOTO_LAYER_NAME } from "../components/layers/SeamlessPhotoTileLayer";
import {
  DATED_PHOTO_FOLDER_TITLE,
  GSI_ATTRIBUTION_URL,
  GSI_HISTORICAL_PHOTO_LAYERS,
  NENDO_PHOTO_LAYER_NAME,
  NENDO_PHOTO_MIN_ZOOM,
  NENDO_PHOTO_YEARS,
} from "./gsiPhotoLayers";

export const PHOTO_SECTION_TITLE = "写真";

const GSI_ATTRIBUTION = {
  attributionText: "国土地理院",
  attributionUrl: GSI_ATTRIBUTION_URL,
} as const;

/**
 * レイヤーコントロールの「写真」セクションを組み立てる。
 *
 * 構成:
 *   写真
 *     ☐ 全国最新写真（国土地理院）      … 衛星画像と合成し全ズームで表示
 *     ▸ 年代別の写真（フォルダ）
 *         ☐ 年度別空中写真（2007年以降） … 年度は折りたたみ式の縦リスト
 *         ☐ 1987年～1990年 … ☐ 1928年頃
 *
 * 年代別の写真の定義は `gsiPhotoLayers.ts` を唯一のソースとし、ここでは UI 項目へ変換するだけにする。
 */
export const buildPhotoOverlayGroup = (): OverlayGroup => {
  const seamlessPhoto: LayerDescriptor = {
    name: SEAMLESS_PHOTO_LAYER_NAME,
    label: SEAMLESS_PHOTO_LAYER_NAME,
    description:
      "国土地理院が提供する全国最新の空中写真（シームレス）を表示します。ズームレベル13以下では衛星画像（全国ランドサットモザイク画像・世界衛星モザイク画像）に切り替わります。",
    minZoom: MAP_MIN_ZOOM,
    maxZoom: MAP_MAX_ZOOM,
    ...GSI_ATTRIBUTION,
  };

  const nendoPhoto: LayerDescriptor = {
    type: "year",
    name: NENDO_PHOTO_LAYER_NAME,
    label: NENDO_PHOTO_LAYER_NAME,
    years: [...NENDO_PHOTO_YEARS],
    yearsLayout: "list",
    description:
      "国土地理院が提供する空中写真を撮影年度ごとにまとめたものです。撮影範囲は年度により異なります。",
    minZoom: NENDO_PHOTO_MIN_ZOOM,
    maxZoom: MAP_MAX_ZOOM,
    ...GSI_ATTRIBUTION,
  };

  const historicalPhotos: LayerDescriptor[] = GSI_HISTORICAL_PHOTO_LAYERS.map(
    (layer) => ({
      name: layer.name,
      label: layer.label,
      description: layer.description,
      minZoom: layer.minZoom,
      maxZoom: MAP_MAX_ZOOM,
      ...GSI_ATTRIBUTION,
    })
  );

  const datedPhotoFolder: LayerFolder = {
    type: "folder",
    title: DATED_PHOTO_FOLDER_TITLE,
    items: [nendoPhoto, ...historicalPhotos],
  };

  return {
    title: PHOTO_SECTION_TITLE,
    layers: [seamlessPhoto, datedPhotoFolder],
  };
};
